import { useMemo, useState } from 'react';
import { useCan } from '@/hooks/use-scopes';
import { useQuery } from '@tanstack/react-query';
import { Link, useNavigate } from 'react-router-dom';
import { Plus, FileText, Search } from 'lucide-react';
import { Card, PageHeader } from '@/components/ui/Card';
import Button from '@/components/ui/Button';
import ViewToggle, { useViewMode } from '@/components/ui/ViewToggle';
import { qk, fetchStrategies } from '@/lib/queries';
import { cn } from '@/lib/cn';

const STATUS_COLOR: Record<string, string> = {
  draft:    'bg-slate-700/40 text-slate-300',
  active:   'bg-emerald-500/15 text-emerald-300',
  done:     'bg-accent-600/20 text-accent-400',
  archived: 'bg-slate-700/40 text-slate-400',
};

export default function StrategiesListPage() {
  const navigate = useNavigate();
  const { data, isLoading } = useQuery({ queryKey: qk.strategies, queryFn: fetchStrategies });
  const [view, setView] = useViewMode('strategies', 'cards');
  const [q, setQ] = useState('');
  const canWrite = useCan('strategies.write');

  const filtered = useMemo(() => {
    const list = data ?? [];
    if (!q.trim()) return list;
    const needle = q.trim().toLowerCase();
    return list.filter((s) =>
      s.code.toLowerCase().includes(needle) ||
      s.title.toLowerCase().includes(needle) ||
      s.client_name.toLowerCase().includes(needle) ||
      s.status.toLowerCase().includes(needle),
    );
  }, [data, q]);

  return (
    <div>
      <PageHeader
        title="Strategies"
        subtitle="Each strategy = one campaign with TOF/MOF/BOF mix + creative output plan"
        actions={
          <div className="flex items-center gap-2">
            <ViewToggle mode={view} onChange={setView} />
            {canWrite && <Link to="/strategies/new"><Button icon={<Plus size={14} />}>New strategy</Button></Link>}
          </div>
        }
      />

      {(data?.length ?? 0) > 0 && (
        <div className="relative mb-4 max-w-md">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted pointer-events-none" />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search by code, title, client, or status..."
            className="w-full h-10 pl-9 pr-3 rounded-lg border border-line bg-bg-card text-sm text-muted-strong placeholder:text-muted/60 focus:outline-none focus:border-accent-400 focus:ring-1 focus:ring-accent-400/40"
          />
        </div>
      )}

      {isLoading && <div className="text-muted text-sm">Loading...</div>}

      {!isLoading && (data?.length ?? 0) === 0 && (
        <Card>
          <div className="text-center py-8 space-y-3">
            <FileText className="mx-auto text-muted" size={36} />
            <p className="text-muted text-sm">No strategies yet.</p>
            {canWrite
              ? <Link to="/strategies/new"><Button>Create your first strategy</Button></Link>
              : <p className="text-xs text-muted/70">A strategist or an admin plans the campaigns.</p>}
          </div>
        </Card>
      )}

      {!isLoading && (data?.length ?? 0) > 0 && filtered.length === 0 && (
        <Card>
          <div className="text-center py-6 text-sm text-muted">No strategies match "{q}".</div>
        </Card>
      )}

      {!isLoading && filtered.length > 0 && view === 'cards' && (
        <div className="space-y-2">
          {filtered.map((s) => (
            <Link key={s.id} to={`/strategies/${s.id}`}>
              <Card className="hover:border-accent-400/50 transition cursor-pointer">
                <div className="flex items-center justify-between gap-4">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-3 mb-1">
                      <span className="text-xs font-mono text-accent-400">{s.code}</span>
                      <span className="text-[10px] uppercase tracking-wide text-muted">{s.status}</span>
                    </div>
                    <div className="text-sm text-muted-strong truncate">{s.title}</div>
                    <div className="text-xs text-muted truncate">{s.client_name}</div>
                  </div>
                  <div className="text-right text-xs text-muted shrink-0">
                    <div>TOF {s.funnel_mix.TOF} &middot; MOF {s.funnel_mix.MOF} &middot; BOF {s.funnel_mix.BOF}</div>
                    <div>{s.slots.length} creative slot{s.slots.length === 1 ? '' : 's'}</div>
                  </div>
                </div>
              </Card>
            </Link>
          ))}
        </div>
      )}

      {!isLoading && filtered.length > 0 && view === 'table' && (
        <Card>
          <div className="overflow-x-auto -mx-5 px-5">
            <table className="w-full text-sm border-collapse">
              <thead>
                <tr className="text-left text-[11px] uppercase tracking-wider text-muted border-b border-line">
                  <th className="font-medium py-2 pr-3 whitespace-nowrap">Code</th>
                  <th className="font-medium py-2 pr-3">Title</th>
                  <th className="font-medium py-2 pr-3 whitespace-nowrap hidden md:table-cell">Client</th>
                  <th className="font-medium py-2 pr-3 whitespace-nowrap hidden md:table-cell">Funnel mix</th>
                  <th className="font-medium py-2 pr-3 whitespace-nowrap hidden lg:table-cell">Slots</th>
                  <th className="font-medium py-2 whitespace-nowrap text-right">Status</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((s) => (
                  <tr
                    key={s.id}
                    onClick={() => navigate(`/strategies/${s.id}`)}
                    className="cursor-pointer border-b border-line last:border-0 hover:bg-bg-soft transition"
                  >
                    <td className="py-3 pr-3 font-mono text-xs text-accent-400 whitespace-nowrap">{s.code}</td>
                    <td className="py-3 pr-3 text-muted-strong">{s.title}</td>
                    <td className="py-3 pr-3 text-xs text-muted whitespace-nowrap hidden md:table-cell">{s.client_name}</td>
                    <td className="py-3 pr-3 text-xs text-muted whitespace-nowrap hidden md:table-cell">
                      <span className="font-mono">{s.funnel_mix.TOF}/{s.funnel_mix.MOF}/{s.funnel_mix.BOF}</span>
                    </td>
                    <td className="py-3 pr-3 text-xs text-muted whitespace-nowrap hidden lg:table-cell">{s.slots.length}</td>
                    <td className="py-3 text-right whitespace-nowrap">
                      <span className={cn(
                        'inline-block text-[10px] font-semibold uppercase tracking-wider px-2 py-0.5 rounded-full',
                        STATUS_COLOR[s.status] ?? STATUS_COLOR.draft,
                      )}>
                        {s.status}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}
    </div>
  );
}
